<template>
	<section>
		<el-row class="titlerow">
			<el-col :sm="12">
				<h1>Willkommen zurück, {{ user.firstName }}!</h1>
			</el-col>
			<el-col :sm="12" class="button-panel">
				<a
					href="https://www.werberat.at/pre_Copy_Advice.aspx"
					target="_blank"
					class="el-button el-button--danger is-round"
					title="Pre-Copy-Advice - der Vorabcheck"
				>
					Pre-Copy-Advice
				</a>

				<a
					href="https://www.werberat.at/show_4315.aspx"
					target="_blank"
					class="el-button el-button--danger is-round"
					title="Pro-Ethik-Siegel erhalten"
				>
					Pro-Ethik-Siegel erhalten
				</a>

				<a href="https://www.werberat.at/ethikkodex.aspx" target="_blank" class="el-button el-button--danger is-round" title="Ethikkodex ansehen">
					Ethikkodex ansehen
				</a>
			</el-col>
		</el-row>
	</section>

	<section class="mobile-hidden">
		<TheBox :title="`Herzlich willkommen im neuen Entscheidungstool!`">
			<el-row>
				<el-col :xs="24" :sm="16">
					<p>
						Das neue Tool soll Ihnen einen schnellen und übersichtlichen Entscheidungsprozess auf allen Devices (online & mobil) ermöglichen. Eine
						einfache Ausführung und klares Design stehen dabei im Vordergrund.
					</p>
					<p>
						Natürlich bedeutet ein neues Tool eine Umstellung. Für eine erleichterte Eingewöhnung haben wir eine “Schritt für Schritt”-Anleitung
						erstellt. Diese finden Sie
						<a
							href="https://beschwerde.werberat.at/werberat-backend/show/e2148e53-bb51-4757-94ce-01f3ff9ea974/Tool_DE.mp4"
							target="_blank"
							title="Schritt für Schritt-Anleitung als Video"
							class="link"
						>
							hier als Video
						</a>
						und in der Form von 3 Info-PDFs:
					</p>
					<ul>
						<li>
							<a
								href="https://beschwerde.werberat.at/werberat-backend/show/0bdb65a2-f618-49a9-844f-43faa00c56ca/Infos_Login_Dashboard.pdf"
								target="_blank"
								title="Einführung in das neue Tool"
								class="link"
							>
								Einführung in das neue Tool
							</a>
						</li>
						<li>
							<a
								href="https://beschwerde.werberat.at/werberat-backend/show/de543de8-4cc4-435a-8086-6ac28d593c17/Beschwerden_bearbeiten.pdf"
								target="_blank"
								title="Beschwerden bearbeiten und Entscheidungen treffen"
								class="link"
							>
								Beschwerden bearbeiten und Entscheidungen treffen
							</a>
						</li>
						<li>
							<a
								href="https://beschwerde.werberat.at/werberat-backend/show/8195712f-1243-4dac-beb7-c751a4e4b6dd/Uebersicht_Entscheidungen.pdf"
								target="_blank"
								title="Übersicht Entscheidungen"
								class="link"
							>
								Übersicht Entscheidungen
							</a>
						</li>
					</ul>

					<p>Viel Spaß bei der neuen Experience!</p>
				</el-col>

				<el-col :xs="24" :sm="8" class="flex flex--justifyend logo-container">
					<img src="../assets/logo_werberat.svg" alt="Werberat Logo" />
				</el-col>
			</el-row>
		</TheBox>
	</section>

	<div class="bg--white">
		<section id="dashfilter">
			<h2>Fälle</h2>
			<DashFilter :mainfilters="mainfilters" @toggleMainFilter="null" @toggleSubFilter="toggleSubFilter" />

			<el-row class="flex flex--aligncenter">
				<el-col :span="22">
					<h2 class="no-margin">Fälle ({{ $getCountErgebnisseLabel(casesTotalCount) }})</h2>
				</el-col>
				<el-col :span="2" class="flex flex--justifyend" v-if="isMobile">
					<SortIcon class="sorticon" @click="openTableSortDrawer = true" />
				</el-col>
			</el-row>

			<CasesTable
				:tableData="filteredCases"
				:loading="caseTableLoading"
				:totalCount="casesTotalCount"
				@loadNextPage="loadNextPage"
				:mobileBox="true"
				:sort="tableSort"
			/>

			<TableSortDrawer v-model:open="openTableSortDrawer" :currentSort="tableSort" @setSort="updateSort" />
		</section>
	</div>
</template>

<script lang="ts">
import { searchCases } from '@/requests/searches';
import { Plus } from '@element-plus/icons-vue';
import { defineComponent, PropType, computed } from 'vue';
import DashFilter, { FilterItem } from '../components/DashFilter.vue';
import CasesTable from '../components/tables/CasesTable.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import SortIcon from '../assets/icons/SortIcon.vue';
import { authUser, useStore } from '../store';
import { CaseFilters, CaseResponse, SearchCaseRequest } from '../types/types';
import { texts } from '../utils/texts';
import TableSortDrawer from '../components/TableSortDrawer.vue';
import { TableSort } from '../types/types';

export default defineComponent({
	name: 'DashboardWerberatView',
	components: {
		TheBox,
		TheButton,
		CasesTable,
		DashFilter,
		SortIcon,
		Plus,
		TableSortDrawer,
	},
	setup() {
		const store = useStore();
		const isMobile = computed(() => store.state.isMobile);
		return {
			store,
			user: authUser.value,
			isMobile,
		};
	},
	data() {
		return {
			filteredCases: [] as CaseResponse[],
			casesTotalCount: 0,
			caseTableLoading: false,
			tablePage: 0,
			itemsPerPage: 14,
			mainfilters: [
				{
					key: 'case',
					label: 'Fälle',
					active: true,
					subfilters: [
						{ key: 'IsInSmallSenate', label: 'Fälle im Kleinen Senat', active: false, count: 0 },
						{ key: 'IsDecisionOpen', label: 'abzustimmende Fälle', active: true, count: 0 },
						{ key: 'IsDecisionDoneButRunning', label: 'abgestimmt, Frist läuft noch', active: false, count: 0 },
						{ key: 'IsDecisionDone', label: 'zugeordnete Entscheidungen', active: false, count: 0 },
					],
				},
			] as FilterItem[],
			casesFilters: [] as any[],
			openTableSortDrawer: false,
			tableSort: { prop: 'voteEndDate', order: 'ascending' } as TableSort, // Current sort option
		};
	},
	computed: {
		texts() {
			return texts;
		},
		mainfilter_active(): FilterItem {
			return this.mainfilters[0]; // always 'case'
		},
		userIsInSmallSenate() {
			const foundRole = this.user.roles.find((role) => role.name.includes('Kleiner Senat'));
			return !!foundRole;
		},
	},
	methods: {
		async loadCases(filters: CaseFilters[] = []) {
			this.caseTableLoading = true;
			const dashboardSearch: SearchCaseRequest = {
				searchTerm: null,
				fromDate: null,
				toDate: null,
				states: null,
				isPublished: null,
				category: null,
				medium: null,
				reach: null,
				sector: null,
			};

			this.mainfilters[0].subfilters.forEach((f) => {
				if (f.active) {
					dashboardSearch[f.key] = true;
				}
			});

			const caseResult = await searchCases(dashboardSearch, this.tablePage, 20, true);

			if (caseResult.success) {
				this.casesTotalCount = caseResult.data.totalCount;
				if (this.tablePage === 0) {
					this.filteredCases = caseResult.data.items;
				} else {
					this.filteredCases = [...this.filteredCases, ...caseResult.data.items];
				}

				this.mainfilters = this.mainfilters.map((m) => {
					if (m.key === 'case') {
						m.subfilters = m.subfilters.map((f) => {
							if (f.key === 'IsInSmallSenate') {
								f.count = caseResult.data.counts.find((c) => c.item1 === 'IsInSmallSenate')?.item2 || 0;
							} else if (f.key === 'IsDecisionOpen') {
								f.count = caseResult.data.counts.find((c) => c.item1 === 'IsDecisionOpen')?.item2 || 0;
							} else if (f.key === 'IsDecisionDoneButRunning') {
								f.count = caseResult.data.counts.find((c) => c.item1 === 'IsDecisionDoneButRunning')?.item2 || 0;
							} else if (f.key === 'IsDecisionDone') {
								f.count = caseResult.data.counts.find((c) => c.item1 === 'IsDecisionDone')?.item2 || 0;
							}
							return f;
						});
					}
					return m;
				});
			} else {
				this.filteredCases = [];
				this.casesTotalCount = 0;
			}

			this.caseTableLoading = false;
		},
		async loadNextPage() {
			this.tablePage++;
			const filters = this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key);
			this.loadCases(filters);
		},
		toggleSubFilter(filter) {
			this.tablePage = 0;
			const findSub = this.mainfilter_active.subfilters.find((f) => f.key === filter);
			if (findSub) {
				findSub.active = !findSub.active;
			}

			const filters = this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key);
			this.loadCases(filters);
		},
		updateSort(sort: TableSort) {
			this.tableSort = sort;
		},
	},
	mounted() {
		this.loadCases();
		if (!this.userIsInSmallSenate) {
			this.mainfilters[0].subfilters = this.mainfilters[0].subfilters.filter((f) => f.key !== 'IsInSmallSenate');
		}
	},
});
</script>

<style lang="scss" scoped>
@use '../scss/vars.scss' as *;

#dashboard {
	.dashfilter {
		padding-top: 0;
	}

	.logo-container {
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: center;

		img {
			width: 80%;
			margin-bottom: 4rem;
			margin-left: 2rem;
		}
	}

	#dashfilter {
		.el-row.flex {
			margin-bottom: 1.5rem;
		}
	}
}
</style>
